import { useQuery } from '@tanstack/react-query'
import { invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { POLL_NORMAL } from '@/api/polling'
import { invoiceKeys } from '@/features/invoices/keys'
import type { components } from '@/api/schema'
import {
  USE_MOCK_AP_INVOICES,
  MOCK_AP_INVOICE_STAFF,
  mockInvoiceDetail,
  mockInvoiceRows,
  getMockVendorCredits,
  getMockReviewQueueCounts,
  getMockInvoices,
  getMockInvoice,
} from './mock'
import type {
  ApInvoiceReviewQueueCounts,
  ApVendorAlias,
  ApVendorAuditEvent,
  ApInvoiceListParams,
  ApInvoiceThreadTemplate,
  ApInvoicePoComparison,
  ApInvoiceProgress,
} from './types'
import type {
  ApOperationalHealth,
  ApVendorSummary,
  ApVendorMergeReview,
  ApVendorCreditBalance,
  ApMatchedDocument,
  ApTaxAdjustmentPreview,
  ApInvoiceDetail,
  ApInvoiceApprovalCapabilities,
  PaginatedApInvoices,
  ApInvoiceThreadDefaults,
} from './view-models'

type AutomationChange = components['schemas']['AutomationChangeRead']

function buildQuery(params?: object): string {
  if (!params) return ''
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    searchParams.append(key, String(value))
  }
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

export function useInvoiceReviewCounts() {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.reviewQueues,
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return getMockReviewQueueCounts()
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceReviewQueueCounts>(token, '/dashboard/ap-invoices/review-queues')
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useApOperationalHealth() {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.operationalHealth,
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          generated_at: new Date().toISOString(),
          approval: { mode: 'preview' },
          readiness: { environment: 'development', capabilities: {} },
          mailbox: { mode: 'dry_run' },
          ai_extraction: { mode: 'disabled' },
          monday_registry: { mode: 'disabled' },
          payment_reconciliation: { mode: 'provider_neutral' },
          reminders: { mode: 'preview' },
          payment_deadline_digest: { mode: 'preview' },
          latest_runs: {},
        } satisfies ApOperationalHealth
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApOperationalHealth>(token, '/dashboard/ap-invoices/operational-health')
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useInvoices(params?: ApInvoiceListParams) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return getMockInvoices(params)
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<PaginatedApInvoices>(token, `/dashboard/ap-invoices${buildQuery(params)}`)
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useInvoice(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.detail(uuid),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return getMockInvoice(uuid)
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(token, `/dashboard/ap-invoices/${uuid}`)
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

/** Who turned this invoice's automation on or off, newest first (USA-1640). */
export function useInvoiceAutomationHistory(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.automationHistory(uuid),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return []
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<AutomationChange[]>(token, `/dashboard/ap-invoices/${uuid}/automation-history`)
    },
    enabled: !!uuid,
  })
}

export function useInvoiceMatchedDocuments(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.matchedDocuments(uuid),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return (getMockInvoice(uuid).po_links ?? [])
          .filter((link) => Boolean(link.purchase_order?.pdf_filename))
          .map((link) => ({
            key: `purchase-order:${link.purchase_order?.uuid}`,
            kind: 'purchase_order' as const,
            name: link.purchase_order?.pdf_filename || `PO-${link.po_number_raw || 'document'}.pdf`,
            source_type: 'purchase_order_snapshot',
            version: null,
            captured_at: null,
            download_path: `/dashboard/purchase-orders/${link.purchase_order?.uuid}/pdf`,
            available: true,
          })) satisfies ApMatchedDocument[]
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApMatchedDocument[]>(
        token,
        `/dashboard/ap-invoices/${uuid}/matched-documents`,
      )
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function useInvoicePoComparison(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.poComparison(uuid),
    queryFn: async (): Promise<ApInvoicePoComparison> => {
      if (USE_MOCK_AP_INVOICES) {
        const invoice = getMockInvoice(uuid)
        const purchaseOrders = (invoice.po_links ?? []).flatMap((link) =>
          link.purchase_order
            ? [{ uuid: link.purchase_order.uuid, po_number: link.purchase_order.po_number, total: link.purchase_order.total }]
            : [],
        )
        const poTotal = purchaseOrders.reduce((sum, po) => sum + Number(po.total ?? 0), 0)
        const difference = Number(invoice.invoice_total ?? 0) - poTotal
        return {
          result: !purchaseOrders.length ? 'no_po' : Math.abs(difference) <= 5 ? 'matches' : difference > 0 ? 'over' : 'under',
          invoice_total: invoice.invoice_total == null ? null : String(invoice.invoice_total),
          po_total: purchaseOrders.length ? poTotal.toFixed(2) : null,
          billed_total: invoice.invoice_total == null ? null : String(invoice.invoice_total),
          difference: purchaseOrders.length ? difference.toFixed(2) : null,
          purchase_orders: purchaseOrders.map((po) => ({ ...po, total: po.total == null ? null : String(po.total) })),
          other_invoices: [],
          lines: [],
          lines_total: null,
          sales_tax: invoice.tax_amount == null ? null : String(invoice.tax_amount),
          terms_differ: false,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoicePoComparison>(token, `/dashboard/ap-invoices/${uuid}/po-comparison`)
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

/** Where the invoice is in approval and payment, and why any step was skipped (USA-1632). */
export function useInvoiceProgress(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.progress(uuid),
    queryFn: async (): Promise<ApInvoiceProgress> => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceProgress>(token, `/dashboard/ap-invoices/${uuid}/progress`)
    },
    enabled: !!uuid && !USE_MOCK_AP_INVOICES,
    refetchInterval: POLL_NORMAL,
  })
}

export function useInvoiceTaxAdjustmentPreview(
  uuid: string,
  stateCodes: readonly string[] = ['TX'],
) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.taxAdjustment(uuid, stateCodes),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          flagged: false,
          rule: 'vendor_tax_policy_unknown',
          vendor_tax_policy: 'unknown',
          available_states: [],
          missing_states: [...stateCodes],
          payment_execution: 'not_performed',
          outbound_message: 'not_sent',
        } satisfies ApTaxAdjustmentPreview
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      const search = new URLSearchParams()
      stateCodes.forEach((state) => search.append('state_codes', state))
      return apiFetch<ApTaxAdjustmentPreview>(
        token,
        `/dashboard/ap-invoices/${uuid}/tax-adjustment/preview?${search.toString()}`,
      )
    },
    enabled: !!uuid,
  })
}

export function useInvoiceApprovalCapabilities(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.approvalCapabilities(uuid),
    queryFn: async (): Promise<ApInvoiceApprovalCapabilities> => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          enforcement_enabled: true,
          policy_version: '2026-08-v2',
          actor_role: 'accounting',
          allowed_decisions: ['approve_accounting'],
          finance_reasons: [],
          blocking_reasons: [],
          self_approval_conflict: false,
          payment_evidence_allowed: false,
          operations_owner: MOCK_AP_INVOICE_STAFF[1],
          operations_project: null,
          ownership_source: 'po_link',
          ownership_error: null,
        } satisfies ApInvoiceApprovalCapabilities
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceApprovalCapabilities>(
        token,
        `/dashboard/ap-invoices/${uuid}/approval-capabilities`,
      )
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function useVendorInvoices(
  vendorId: number | string | undefined,
  params?: ApInvoiceListParams,
) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendorInvoices(vendorId, params),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        const vendor = mockInvoiceDetail().vendor
        return {
          ...getMockInvoices(params),
          items: mockInvoiceRows().filter(
            (invoice) =>
              String(invoice.vendor?.id) === String(vendorId) || vendorId === vendor?.uuid,
          ),
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<PaginatedApInvoices>(
        token,
        `/dashboard/ap-invoices/vendors/${vendorId}/invoices${buildQuery(params)}`,
      )
    },
    enabled: vendorId !== undefined && vendorId !== null && String(vendorId).length > 0,
    refetchInterval: POLL_NORMAL,
  })
}

export function useApVendor(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendor(vendorId),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return mockInvoiceDetail().vendor as ApVendorSummary
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorSummary>(token, `/dashboard/ap-invoices/vendors/${vendorId}`)
    },
    enabled: vendorId !== undefined && vendorId !== null && String(vendorId).length > 0,
    refetchInterval: POLL_NORMAL,
  })
}

export function useApVendors(params?: {
  search?: string
  include_inactive?: boolean
  limit?: number
}) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendors(params),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        const vendor = mockInvoiceDetail().vendor
        return vendor ? [vendor] : []
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      const query = new URLSearchParams()
      if (params?.search) query.set('search', params.search)
      if (params?.include_inactive) query.set('include_inactive', 'true')
      if (params?.limit) query.set('limit', String(params.limit))
      const suffix = query.size ? `?${query.toString()}` : ''
      return apiFetch<ApVendorSummary[]>(token, `/dashboard/ap-invoices/vendors${suffix}`)
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useVendorAliases(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendorAliases(vendorId),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return [] satisfies ApVendorAlias[]
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorAlias[]>(
        token,
        `/dashboard/ap-invoices/vendors/${vendorId}/aliases?include_inactive=true`,
      )
    },
    enabled: vendorId !== undefined && vendorId !== null && String(vendorId).length > 0,
    refetchInterval: POLL_NORMAL,
  })
}

export function useVendorAudit(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendorAudit(vendorId),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return [] satisfies ApVendorAuditEvent[]
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorAuditEvent[]>(
        token,
        `/dashboard/ap-invoices/vendors/${vendorId}/audit`,
      )
    },
    enabled: vendorId !== undefined && vendorId !== null && String(vendorId).length > 0,
    refetchInterval: POLL_NORMAL,
  })
}

export function useVendorMergeReviews(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendorMergeReviews(vendorId),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return [] satisfies ApVendorMergeReview[]
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorMergeReview[]>(
        token,
        `/dashboard/ap-invoices/vendor-merge-reviews?vendor_id=${vendorId}`,
      )
    },
    enabled: vendorId !== undefined && vendorId !== null && String(vendorId).length > 0,
    refetchInterval: POLL_NORMAL,
  })
}

export function useVendorCredits(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: invoiceKeys.vendorCredits(vendorId),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return getMockVendorCredits(vendorId)
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorCreditBalance>(
        token,
        `/dashboard/ap-invoices/vendors/${vendorId}/credits`,
      )
    },
    enabled: vendorId !== undefined && vendorId !== null && String(vendorId).length > 0,
    refetchInterval: POLL_NORMAL,
  })
}

export function useInvoiceThreadDefaults(
  uuid: string,
  template: ApInvoiceThreadTemplate = 'ap_invoice',
) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: [...invoiceKeys.detail(uuid), 'thread-defaults', template],
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          email: 'accounting@shopusars.com',
          name: 'USA-RS Accounting',
          cc_emails: ['purchasing@shopusars.com'],
        } satisfies ApInvoiceThreadDefaults
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceThreadDefaults>(
        token,
        `/dashboard/ap-invoices/${uuid}/default-recipients?template=${template}`,
      )
    },
    enabled: !!uuid,
  })
}
